import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Check, ChevronDown } from 'lucide-react';
import { useTranslation, setAppLanguage } from '@/i18n/useTranslation';
import type { Language } from '@/i18n/translations';
import { switchLocaleInPath } from '@/i18n/localeRouting';
import { useAppNavigate } from '@/hooks/useAppNavigate';

interface LanguageToggleProps {
  variant?: 'mobile' | 'sidebar';
  disabled?: boolean;
}

const languages: { id: Language; flag: string }[] = [
  { id: 'pl', flag: '🇵🇱' },
  { id: 'en', flag: '🇬🇧' },
];

export default function LanguageToggle({ variant = 'mobile', disabled = false }: LanguageToggleProps) {
  const { language, t } = useTranslation();
  const location = useLocation();
  const navigate = useAppNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const isSidebar = variant === 'sidebar';

  const selected = languages.find((lang) => lang.id === language) ?? languages[0];

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    }
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  useEffect(() => {
    function handleEsc(e: KeyboardEvent) {
      if (e.key === 'Escape') setIsOpen(false);
    }
    if (isOpen) {
      document.addEventListener('keydown', handleEsc);
    }
    return () => document.removeEventListener('keydown', handleEsc);
  }, [isOpen]);

  const handleSelect = (lang: Language) => {
    if (disabled || lang === language) {
      setIsOpen(false);
      return;
    }
    setAppLanguage(lang);
    navigate(switchLocaleInPath(location.pathname, location.search, location.hash, lang), { replace: true });
    setIsOpen(false);
  };

  return (
    <div ref={containerRef} className={`relative ${isSidebar ? 'w-full' : ''}`}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setIsOpen((open) => !open)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={t('language.label')}
        title={t('language.label')}
        className={`flex items-center gap-1 border border-border bg-bg-control transition-all ${
          isSidebar ? 'w-full h-9 rounded-lg pl-3 pr-2 justify-between' : 'h-9 min-w-[72px] rounded-xl px-2.5 justify-center'
        } ${
          disabled
            ? 'opacity-40 cursor-not-allowed'
            : isOpen
              ? 'border-accent shadow-[0_0_0_2px_var(--accent-light)]'
              : 'hover:border-text-disabled hover:bg-bg-control-hover'
        }`}
      >
        <span className="flex items-center gap-1">
          <span className="text-base leading-none">{selected.flag}</span>
        </span>
        <ChevronDown
          className={`w-3 h-3 text-text-disabled transition-transform ${isOpen ? 'rotate-180' : ''}`}
          strokeWidth={2}
        />
      </button>

      {isOpen && (
        <div
          role="listbox"
          aria-label={t('language.label')}
          className={`absolute z-[200] min-w-[148px] bg-bg-panel border border-border rounded-lg shadow-lg overflow-hidden ${
            isSidebar
              ? 'bottom-full left-0 mb-2'
              : 'bottom-full left-1/2 -translate-x-1/2 mb-2'
          }`}
        >
          {languages.map((lang) => {
            const isActive = language === lang.id;
            return (
              <button
                key={lang.id}
                type="button"
                role="option"
                aria-selected={isActive}
                onClick={() => handleSelect(lang.id)}
                className={`w-full flex items-center gap-2 pl-4 pr-3 py-2 text-left transition-colors ${
                  isActive ? 'bg-accent-light' : 'hover:bg-bg-control'
                }`}
              >
                <span className="text-base leading-none">{lang.flag}</span>
                <span className="flex-1 text-xs font-medium text-text-primary">
                  {t(lang.id === 'en' ? 'language.en' : 'language.pl')}
                </span>
                {isActive && (
                  <Check className="w-3.5 h-3.5 text-accent flex-shrink-0" strokeWidth={2.5} />
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
